import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import clsx from "clsx";
import { type RefObject, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { GripVertical, Plus, X } from "react-bootstrap-icons";
import type { MiscContent } from "@/briefpoint-client";
import Button from "@/components/Button";
import CustomEditor from "@/components/Editor/CustomEditor";
import { FormModal } from "@/components/Modals/Modal";
import { useAuth } from "@/hooks/useAuth";
import styles from "./GeneralObjections.module.scss";

/* MiscContent.content is a JSON string with the following structure:
 * {
 *   "Intro": "string",
 *   "Items": [
 *     {
 *       "Id": string,
 *       "Order": number,
 *       "Content": string
 *     }
 *   ]
 * }
 */
export interface GeneralObjection {
  // Uppercase because this is the name of the field in the JSON
  Id?: string; // New items don't have an ID
  Order: number;
  Content: string | null;
}

export interface EmptyGeneralObjection {
  Id: string;
  Order: number | null;
  Content: string | null;
  empty: true;
  parentNumber: number;
}

type CombinedList = (GeneralObjection | EmptyGeneralObjection)[];

function isEmptyGeneralObjection(
  objection: GeneralObjection | EmptyGeneralObjection,
): objection is EmptyGeneralObjection {
  return Boolean(
    (objection as EmptyGeneralObjection).empty &&
      typeof (objection as EmptyGeneralObjection).parentNumber === "number",
  );
}

interface Props {
  generalObjections: MiscContent;
  onUpdate: (update: MiscContent) => Promise<MiscContent>;
}

export default function GeneralObjections({
  generalObjections,
  onUpdate,
}: Props) {
  const [list, setList] = useState<CombinedList>([]);
  const [intro, setIntro] = useState<string>("");
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  const [newItems, setNewItems] = useState<Map<number, number>>(new Map());
  const { user, updatePreferences } = useAuth();
  const [showInputs, setShowInputs] = useState<boolean>(false);

  useEffect(() => {
    if (generalObjections?.content) {
      const objectionList: GeneralObjection[] = [];
      const json = JSON.parse(generalObjections.content);
      for (const item of json.Items) {
        objectionList.push({
          Id: item.Order,
          Order: item.Order,
          Content: item.Content,
        });
      }
      setIntro(json.Intro);
      setList(objectionList);
    }
  }, [generalObjections]);

  useEffect(() => {
    if (user?.preferences) {
      setShowInputs(user?.preferences?.includeGeneralObjections || false);
    }
  }, [user]);

  async function handleCheckChange(checked: boolean) {
    setShowInputs(checked);
    if (user?.preferences) {
      user.preferences.includeGeneralObjections = checked;
      await updatePreferences(user.preferences);
    }
  }

  async function onAdd(newOrder: number, newContent: string) {
    const result = Array.from(list);
    const orderedItemList: CombinedList = [];
    const newItem = { Order: newOrder, Content: newContent };
    result.splice(newOrder, 0, newItem);
    let index = 0;
    for (const item of result) {
      item.Order = index + 1;
      index++;
      orderedItemList.push(item);
    }
    await onUpdate({
      ...generalObjections,
      content: JSON.stringify({ Intro: intro, Items: orderedItemList }),
    });
    setList(orderedItemList);
  }

  async function onEdit(index: number, text: string) {
    list[index - 1].Content = text;
    onUpdate({
      ...generalObjections,
      content: JSON.stringify({ Intro: intro, Items: list }),
    });
  }

  async function handleConfirmDelete() {
    if (confirmRemove !== null) {
      const objection = list[confirmRemove - 1];
      if (objection) {
        const result = Array.from(list);
        result.splice(confirmRemove - 1, 1);
        const orderedItemList: CombinedList = [];
        let index = 0;
        for (const item of result) {
          item.Order = index + 1;
          index++;
          orderedItemList.push(item);
        }
        setList(orderedItemList);
        onUpdate({
          ...generalObjections,
          content: JSON.stringify({ Intro: intro, Items: orderedItemList }),
        });
        const count = objection.Order
          ? (newItems.get(objection.Order) ?? 0)
          : 0;
        if (objection.Order && count > 0) {
          setNewItems(new Map(newItems.set(objection.Order, 0)));
          setNewItems(new Map(newItems.set(objection.Order - 1, count)));
        }
        setConfirmRemove(null);
      }
    }
  }

  function getEmptyObjections(index: number): EmptyGeneralObjection[] {
    const count = newItems.get(index);
    const emptyObjections: EmptyGeneralObjection[] = [];
    if (count) {
      for (let i = 0; i < count; i++) {
        emptyObjections.push({
          Id: `${index}-${i}`,
          Order: null,
          Content: null,
          empty: true,
          parentNumber: index,
        });
      }
    }
    return emptyObjections;
  }

  function removeNewItem(number: number, addNew = false) {
    const count = newItems.get(number) || 0;
    if (addNew) {
      if (count > 0) {
        setNewItems(new Map(newItems.set(number, 0)));
        setNewItems(new Map(newItems.set(number + 1, count - 1)));
      }
    } else {
      if (count > 0) {
        setNewItems(new Map(newItems.set(number, count - 1)));
      }
    }
  }

  const reorder = (
    list: CombinedList,
    startIndex: number,
    endIndex: number,
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    const removedOrder = removed.Order;
    const orderedItemList: CombinedList = [];
    let newIndex = 0;
    result.splice(endIndex, 0, removed);
    let index = 0;
    for (const item of result) {
      if (!isEmptyGeneralObjection(item)) {
        item.Order = index + 1;
        index++;
        if (item.Order === removedOrder) {
          newIndex = item.Order;
        }
        orderedItemList.push(item);
      }
    }
    return { orderedList: orderedItemList, newIndex: newIndex };
  };

  const onDragEnd = (res: DropResult) => {
    const { destination, source } = res;
    if (!destination) return;
    if (destination.index === source.index) return;

    const sourceItem = combinedList[source.index];
    const destinationItem = combinedList[destination.index];
    let parentNumber = isEmptyGeneralObjection(destinationItem)
      ? destinationItem.parentNumber
      : destinationItem.Order;

    if (isEmptyGeneralObjection(sourceItem)) {
      if (
        (parentNumber !== null && sourceItem.parentNumber !== parentNumber) ||
        !isEmptyGeneralObjection(destinationItem)
      ) {
        if (
          destination.index < source.index &&
          !isEmptyGeneralObjection(destinationItem)
        ) {
          parentNumber = parentNumber - 1;
        }
        removeNewItem(sourceItem.parentNumber ?? 0);
        setNewItems(
          new Map(
            newItems.set(parentNumber, (newItems.get(parentNumber) || 0) + 1),
          ),
        );
      }
    } else {
      const newMap = newItems;
      let setNew = false;
      for (let i = 0; i < combinedList.length; i++) {
        const item = combinedList[i];
        if (isEmptyGeneralObjection(item)) {
          setNew = true;
          if (source.index > destination.index) {
            if (i >= destination.index && i < source.index) {
              newMap.set(
                item.parentNumber,
                (newMap.get(item.parentNumber) ?? 0) - 1,
              );
              newMap.set(
                item.parentNumber + 1,
                (newMap.get(item.parentNumber + 1) ?? 0) + 1,
              );
            }
          } else {
            if (i > source.index && i <= destination.index) {
              newMap.set(
                item.parentNumber,
                (newMap.get(item.parentNumber) ?? 0) - 1,
              );
              newMap.set(
                item.parentNumber - 1,
                (newMap.get(item.parentNumber - 1) ?? 0) + 1,
              );
            }
          }
        }
        if (sourceItem.Order !== parentNumber) {
          const { orderedList } = reorder(
            combinedList,
            source.index,
            destination.index,
          );
          setList(orderedList);
          if (setNew) {
            setNewItems(newMap);
          }
          onUpdate({
            ...generalObjections,
            content: JSON.stringify({ Intro: intro, Items: orderedList }),
          });
        } else {
          if (setNew) {
            setNewItems(new Map(newMap));
          }
        }
      }
    }
  };

  let combinedList: CombinedList = getEmptyObjections(0);

  for (const objection of list) {
    combinedList.push(objection);
    if (objection.Order) {
      combinedList = [...combinedList, ...getEmptyObjections(objection.Order)];
    }
  }

  return (
    <div className={styles.interrogatoryListWrapper}>
      <div style={{ marginLeft: "35px" }}>
        <Form.Check
          id="use-general-objections"
          type="checkbox"
          label="Include General Objections in every output document"
          onChange={(e) => handleCheckChange(e.currentTarget.checked)}
          checked={showInputs}
        />
      </div>
      {showInputs ? (
        combinedList.length > 0 ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="interrogatories-list">
              {(provided) => (
                <div
                  className="interrogatories-edit mt-2"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {combinedList.map((objection, index) => (
                    <Draggable
                      key={objection.Id}
                      draggableId={`${objection.Id}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          ref={provided.innerRef}
                          className={clsx("selection-item", {
                            isDragging: snapshot.isDragging,
                          })}
                        >
                          {isEmptyGeneralObjection(objection) ? (
                            <ObjectionAdd
                              onAdd={async (text: string) => {
                                await onAdd(objection.parentNumber, text);
                                removeNewItem(objection.parentNumber, true);
                              }}
                              onDelete={() =>
                                removeNewItem(objection.parentNumber)
                              }
                            />
                          ) : (
                            <ObjectionEdit
                              isLastItem={index + 1 === combinedList.length}
                              objection={objection}
                              onAdd={() => {
                                const count =
                                  newItems.get(objection.Order) || 0;
                                setNewItems(
                                  new Map(
                                    newItems.set(objection.Order, count + 1),
                                  ),
                                );
                              }}
                              onEdit={onEdit}
                              onDelete={() => {
                                setConfirmRemove(objection.Order);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <Button
            variant="outline"
            onClick={() => {
              setNewItems(new Map(newItems.set(0, 1)));
            }}
          >
            <Plus />
            Add new
          </Button>
        )
      ) : null}
      <FormModal
        title="Are you sure?"
        cancelText="Cancel"
        confirmText="Yes, remove"
        show={confirmRemove != null}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleConfirmDelete}
      >
        <div>
          Are you sure you want to remove this objection? This cannot be undone.
        </div>
      </FormModal>
    </div>
  );
}

export function ObjectionEdit({
  isLastItem,
  objection,
  onAdd,
  onEdit,
  onDelete,
}: {
  isLastItem: boolean;
  objection: GeneralObjection;
  onAdd: () => void;
  onEdit: (index: number, text: string) => void;
  onDelete: () => void;
}) {
  const [content, setContent] = useState(objection.Content);

  const [isHovering, setIsHovering] = useState(false);

  const editingRef = useRef<HTMLDivElement | null>(null);

  function handleEditingBlur() {
    if (content !== null && content !== objection.Content && content !== "") {
      onEdit(objection.Order, content);
    }
  }

  function handleOnKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setContent(objection.Content);
      editingRef.current?.blur();
    }
  }

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const handleContentUpdate = (val: string) => {
    setContent(val);
  };

  return (
    <>
      <div
        className={styles.interrogatoryItem}
        onMouseOver={handleMouseOver}
        onFocus={handleMouseOver}
        onBlur={handleMouseOut}
      >
        <div className="main-item">
          <div className="d-flex justify-content-between">
            <div className={styles.interrogatoryTitle}>
              General Objection {objection.Order}
            </div>
            <div>
              <X className={styles.removeItem} onClick={onDelete} />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <GripVertical className={styles.dragSelectionItem} />
            <CustomEditor
              isFullWidth
              editingRef={editingRef as RefObject<HTMLDivElement>}
              handleKeyDown={handleOnKeyDown}
              handleBlur={handleEditingBlur}
              handleChange={handleContentUpdate}
              shouldBlurOnEnter
              editorContent={content ?? ""}
            />
          </div>
        </div>
      </div>
      <div
        className={clsx(styles.addItemWrapper, {
          [styles.hideItem]: !isHovering && !isLastItem,
        })}
        onMouseOver={handleMouseOver}
        onFocus={handleMouseOver}
        onBlur={handleMouseOut}
      >
        <div className={styles.borderLine}>
          <div className={styles.buttonWrapper}>
            <Button variant="outline" onClick={onAdd}>
              <Plus />
              Add new
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export function ObjectionAdd({
  onAdd,
  onDelete,
}: {
  onAdd: (text: string) => Promise<void>;
  onDelete: () => void;
}) {
  const [content, setContent] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch onAdd
  useEffect(() => {
    // Reset state on rebuild.  Not doing this causes other list items to take over
    // the objects ID.  Doing this outside a useEffect causes an "unmounted" error
    setContent("");
    setIsSaving(false);
  }, [onAdd]);

  async function handleBlur() {
    if (content !== "") {
      setIsSaving(true);
      await onAdd(content);
    }
  }

  function handleOnContentUpdate(val: string) {
    setContent(val);
  }

  return (
    <div
      className={`${styles.interrogatoryItem} ${styles.withPaddingBottom} showHover`}
    >
      <div className="main-item">
        <div className="d-flex justify-content-between">
          <div className={styles.interrogatoryTitle}>Adding new item</div>
          <div>
            <X className={styles.removeItem} onClick={onDelete} />
          </div>
        </div>
        <div className="d-flex align-items-center">
          <GripVertical className={styles.dragSelectionItem} />
          <CustomEditor
            forceFocus
            isFullWidth
            isDisabled={isSaving}
            handleChange={handleOnContentUpdate}
            handleBlur={handleBlur}
            editorContent={content}
            shouldBlurOnEnter
          />
        </div>
      </div>
    </div>
  );
}
